import {
  SET_PROJECTS_STATUSES,
  SET_FACETED_DATA,
  SET_FILTERS,
  SET_DATES_RANGE,
  SET_FACETS_MODE,
  SET_SORT_BY,
  SET_IS_LOADING,
  SET_QUERY_VALUE,
} from './mutation-types'
import dataService from 'src/app.service'

const getInternalPropName = title => {
  const lookup = {
    type: 'projectType',
    status: 'status',
    query: 'displayName',
    dateFrom: 'dateFrom',
    dateTo: 'dateTo',
    mode: 'mode',
  }
  return lookup[title] || title
}

const initialMaxDate = new Date(8640000000000).getTime()

const state = {
  projects: [],
  projects_pagination: {},
  filters: {
    type: '',
    dateFrom: new Date(0).getTime(),
    dateTo: initialMaxDate,
    status: ['active', 'done'],
    query: '',
    mode: '',
    sort: '-createdAt',
    dateRange: '',
  },
  faceted: {},
  isLoading: false,
}

const getApiPath = payload => {
  const { pageNumber = 0, itemsPerPage = 20 } = payload

  const paginationOptions = {
    count: itemsPerPage,
    offset: pageNumber * itemsPerPage,
  }
  const paginationString = Object.keys(paginationOptions)
    .map(key => `${key}=${paginationOptions[key]}`)
    .join('&')

  const activeFilters = Object.entries(state.filters).filter(e => {
    if (e[0] === 'dateFrom' || e[0] === 'dateTo') {
      return e
    }
    return e[1] && e[1].length
  })

  const appliedFilters = activeFilters.map(entry => {
    const key = getInternalPropName(entry[0])
    let value = entry[1]
    if (Array.isArray(value)) {
      value = value.join(',')
    }
    if (entry[0] === 'type') {
      value = 'internal.request.' + value
    }
    return `${key}=${value}`
  })

  const queryString = appliedFilters.join('&')
  let apiPath = `?${paginationString}`
  if (queryString) {
    apiPath = apiPath + '&' + queryString
  }
  return apiPath
}

const mutations = {
  [SET_PROJECTS_STATUSES](state, payload) {
    state.projects = payload.items
    state.projects_pagination = payload.pagination
  },
  [SET_FACETED_DATA](state, payload) {
    state.faceted = payload
  },
  [SET_FILTERS](state, payload) {
    state.filters[payload.name] = payload.value
  },
  [SET_DATES_RANGE](state, payload) {
    state.filters.dateFrom = payload.from
    state.filters.dateTo = payload.to
    state.filters.dateRange = payload.range
  },
  [SET_FACETS_MODE](state, payload) {
    state.filters.mode = payload
  },
  [SET_SORT_BY](state, payload) {
    state.filters.sort = payload
  },
  [SET_IS_LOADING](state, payload) {
    state.isLoading = payload
  },
  [SET_QUERY_VALUE](state, payload) {
    state.filters.query = payload
  },
}

const actions = {
  fetchProjectsStatuses({ commit, state }, payload) {
    commit(SET_IS_LOADING, true)
    const apiPath = '/projects-search' + getApiPath(payload)
    return dataService
      .get(apiPath)
      .then(res => {
        if (res.data.items) {
          commit(SET_PROJECTS_STATUSES, {
            items: res.data.items,
            pagination: res.data._pagination,
          })
        }
        const notifications = _.get(res.data, 'settings.notifications')
        if (notifications) {
          commit('settings/SET_NOTIFICATIONS_SETTINGS', notifications, {
            root: true,
          })
        }
      })
      .finally(() => {
        commit(SET_IS_LOADING, false)
      })
  },
  fetchFacetedResults({ commit, state }, payload = {}) {
    commit(SET_IS_LOADING, true)
    const apiPath = '/projects-search/faceted' + getApiPath(payload)
    return dataService
      .get(apiPath)
      .then(res => {
        if (res.data) {
          commit(SET_FACETED_DATA, res.data)
        }
        const notifications = _.get(res.data, 'settings.notifications')
        if (notifications) {
          commit('settings/SET_NOTIFICATIONS_SETTINGS', notifications, {
            root: true,
          })
        }
      })
      .finally(() => {
        commit(SET_IS_LOADING, false)
      })
  },
  sendProjectRequest({ commit }, payload) {
    const authority = {
      origin: { organisation: 'asoko.projects.web' },
      initiator: { type: 'person' },
    }

    const { projectType, _data } = payload

    const requestBody = {
      authority,
      projectType,
      _data,
    }

    return dataService.post('/projects', requestBody)
  },
  updateProjectLabel({ commit }, payload) {
    const { projectId, userLabel } = payload

    const requestBody = {
      userLabel,
    }

    return dataService.put(`/projects/${projectId}/label`, requestBody)
  },
  setProjectsFilters({ commit, dispatch, state }, payload) {
    if (state.filters[payload.name] !== payload.value) {
      commit(SET_FILTERS, payload)
      if (state.filters.query) {
        dispatch('fetchFacetedResults', {})
      } else {
        dispatch('fetchProjectsStatuses', {})
      }
    }
  },
  setDatesRange({ commit, dispatch, state }, payload) {
    if (
      state.filters.dateFrom !== payload.from ||
      state.filters.to !== payload.to
    ) {
      commit(SET_DATES_RANGE, payload)
      if (state.filters.query) {
        dispatch('fetchFacetedResults', {})
      } else {
        dispatch('fetchProjectsStatuses', {})
      }
    }
  },
  setFacetsMode({ commit, dispatch }, payload) {
    commit(SET_FACETS_MODE, payload)
  },
  setQueryValue({ commit, dispatch }, payload) {
    commit(SET_QUERY_VALUE, payload)
  },
  setSortBy({ commit, dispatch }, payload) {
    if (state.filters.sort !== payload) {
      commit(SET_SORT_BY, payload)

      if (state.filters.query) {
        dispatch('fetchFacetedResults', {})
      } else {
        dispatch('fetchProjectsStatuses', {})
      }
    }
  },
  setIsLoading({ commit, dispatch }, payload) {
    commit(SET_IS_LOADING, payload)
  },
}

const getters = {
  projects: state => state.projects,
  projects_pagination: state => state.projects_pagination,
  projectsByType: (state, getters) => type => {
    return getters.projects.filter(project => project.projectType === type)
  },
  projectsStatusFilter: state => state.filters.status,
  projectsTypeFilter: state => state.filters.type,
  projectsDateFilter: state => state.filters.dateRange,
  projectsTextQuery: state => state.filters.query,
  projectsFacetsMode: state => state.filters.mode,
  projectsSelectedSort: state => state.filters.sort,
  facetedData: state => state.faceted,
  isLoading: state => state.isLoading,
}

export default {
  state,
  mutations,
  actions,
  getters,
}
